import { useCallback, useEffect, useRef, useState } from 'react';
import { synthesizeSpeech, type TTSFormat } from '@/api/ttsApi';

type UseTTSStreamOptions = {
  format?: TTSFormat;
  onAudioElementReady?: (audio: HTMLAudioElement) => void;
  onAllAudioComplete?: () => void;
};

/**
 * Simple TTS stream helper.
 * Feed in text chunks, it will build sentences, call TTS per sentence immediately,
 * and play audio in order as each clip becomes ready.
 */
export function useTTSStream(options: UseTTSStreamOptions = {}) {
  const { format = 'mp3', onAudioElementReady, onAllAudioComplete } = options;
  const sentenceBufferRef = useRef<string>('');
  const queueRef = useRef<Array<{
    id: number;
    text: string;
    url?: string;
    ready: boolean;
  }>>([]);
  const idCounterRef = useRef(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isMountedRef = useRef(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const isPlayingRef = useRef(false);
  const pendingSynthesisCountRef = useRef(0);
  const isStreamingCompleteRef = useRef(false);
  const onAllAudioCompleteRef = useRef(onAllAudioComplete);
  
  useEffect(() => {
    onAllAudioCompleteRef.current = onAllAudioComplete;
  }, [onAllAudioComplete]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  const decodeBase64ToUrl = (base64: string, mimeType: string = 'audio/mpeg') => {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mimeType });
    return URL.createObjectURL(blob);
  };

  const checkAllAudioComplete = useCallback(() => {
    if (
      isStreamingCompleteRef.current &&
      queueRef.current.length === 0 &&
      pendingSynthesisCountRef.current === 0 &&
      !isPlayingRef.current
    ) {
      onAllAudioCompleteRef.current?.();
    }
  }, []);

  const tryPlayNext = useCallback(() => {
    if (!isMountedRef.current) return;
    if (isPlayingRef.current) return;
    const next = queueRef.current[0];
    if (!next || !next.ready || !next.url) return;

    isPlayingRef.current = true;
    setIsSpeaking(true);

    const audio = new Audio(next.url);
    audioRef.current = audio;
    onAudioElementReady?.(audio);

    const cleanup = () => {
      URL.revokeObjectURL(next.url!);
      audioRef.current = null;
      queueRef.current.shift();
      isPlayingRef.current = false;
      if (queueRef.current.length === 0) {
        setIsSpeaking(false);
      }
      tryPlayNext();
      checkAllAudioComplete();
    };

    audio.onended = cleanup;
    audio.onerror = cleanup;
    void audio.play().catch(() => {
      cleanup();
    });
  }, [checkAllAudioComplete]);

  const enqueueSentence = useCallback(
    (text: string) => {
      const sentence = text.trim();
      if (!sentence) return;
      const id = idCounterRef.current++;
      const item: { id: number; text: string; ready: boolean; url?: string } = {
        id,
        text: sentence,
        ready: false,
        url: undefined,
      };
      queueRef.current.push(item);
      pendingSynthesisCountRef.current++;

      // Fire TTS immediately (no waiting for previous playback)
      void synthesizeSpeech(sentence, format)
        .then((result) => {
          pendingSynthesisCountRef.current--;
          if (!isMountedRef.current) return;
          if (!result.success || !result.audioBase64) {
            throw new Error(result.error || 'TTS failed');
          }
          item.url = decodeBase64ToUrl(result.audioBase64, result.mimeType);
          item.ready = true;
          if (queueRef.current[0]?.id === item.id) {
            tryPlayNext();
          }
        })
        .catch((err) => {
          pendingSynthesisCountRef.current--;
          item.ready = true; // mark done to avoid blocking queue
          if (queueRef.current[0]?.id === item.id) {
            queueRef.current.shift();
            tryPlayNext();
          }
          checkAllAudioComplete();
        });

      // Try playing in case something is already ready
      tryPlayNext();
    },
    [format, tryPlayNext, checkAllAudioComplete],
  );

  const handleChunk = useCallback(
    (chunk: string) => {
      if (!chunk) return;
      sentenceBufferRef.current += chunk;

      // Extract complete sentences ending with punctuation
      const sentences = sentenceBufferRef.current.match(/[^.!?]+[.!?]/g);
      if (sentences && sentences.length > 0) {
        sentences.forEach(enqueueSentence);
        // Remove processed part from buffer
        const consumed = sentences.join('');
        sentenceBufferRef.current = sentenceBufferRef.current.slice(consumed.length);
      }
    },
    [enqueueSentence],
  );

  /**
   * Flush any remaining text in the buffer as a final sentence.
   * Call this when streaming completes to ensure all text is spoken.
   * Also marks streaming as complete to trigger onAllAudioComplete when done.
   */
  const flush = useCallback(() => {
    isStreamingCompleteRef.current = true;
    const remaining = sentenceBufferRef.current.trim();
    if (remaining) {
      sentenceBufferRef.current = '';
      enqueueSentence(remaining);
    } else {
      // No remaining text, check if we're already done
      checkAllAudioComplete();
    }
  }, [enqueueSentence, checkAllAudioComplete]);

  /**
   * Check if there's any audio pending (queued or playing).
   */
  const hasPendingAudio = useCallback(() => {
    return queueRef.current.length > 0 || isPlayingRef.current;
  }, []);

  return {
    handleChunk,
    flush,
    isSpeaking,
    hasPendingAudio,
  };
}
